"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useOnboardingSteps = exports.REWARD_STEP = exports.STEP_KEYS = void 0;
const onboarding_reward_1 = require("@app/request/onboarding-reward");
const permissions_pool_1 = require("@app/const/permissions-pool");
const onboarding_state_1 = __importDefault(require("@app/request/onboarding-state"));
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const useMe_1 = require("@app/util/hooks/useMe");
const onboarding_signal_1 = require("@app/util/onboarding-signal");
const _ui_1 = require("@ui");
const react_1 = __importDefault(require("react"));
var STEP_KEYS;
(function (STEP_KEYS) {
    STEP_KEYS["POSITIONS"] = "positions";
    STEP_KEYS["EMPLOYEES"] = "employees";
    STEP_KEYS["CONTRACTS"] = "contracts";
    STEP_KEYS["SCHEDULE"] = "schedule";
    STEP_KEYS["PUBLISH"] = "publish";
    STEP_KEYS["TERMINAL"] = "terminal";
    STEP_KEYS["DASHBOARD"] = "dashboard";
    STEP_KEYS["REWARD"] = "reward";
})(STEP_KEYS || (exports.STEP_KEYS = STEP_KEYS = {}));
/**
 * Steps that are built but not shown yet. Delete an entry to re-enable that step — and
 * update STEP_IDS plus the progress-count assertions in `onboarding.cy.ts`, which pin the
 * visible set and will fail on purpose until you do.
 */
const HIDDEN_STEPS = [
    // a physical terminal is not something a manager can set up in the app on the day they
    // sign up, so asking for it in the first-run checklist only ever left a step unfinishable
    STEP_KEYS.TERMINAL,
];
/** the closing bonus card — also drawn on the invoicing page while its coupon is live */
exports.REWARD_STEP = {
    key: 'REWARD',
    id: STEP_KEYS.REWARD,
    titleKey: 'ONB_STEP_REWARD_TITLE',
    descKey: 'ONB_STEP_REWARD_DESC',
    ico: _ui_1.Icon.ICONS.cash,
    isReward: true,
};
/**
 * Websocket events arrive in bursts — publishing a schedule emits one per shift — so a
 * change defers the re-read by this much and collapses the whole burst into a single query.
 */
const SETTLE_MS = 400;
const reducer = (state, action) => {
    switch (action.type) {
        case 'LOADING_START':
            return { ...state, isLoading: true };
        case 'LOADING_END':
            return { ...state, isLoading: false, data: action.payload };
        // deliberately does not touch `isLoading`: a re-read happens under a checklist that is
        // already on screen, and flipping the flag would swap it for the page loader
        case 'REFRESHED':
            return { ...state, data: action.payload };
        default:
            return state;
    }
};
const initialState = {
    isLoading: true,
    data: undefined,
};
/**
 * The single place that decides what „done" means for each step. Split out from the hook so
 * a re-read after a websocket change goes through exactly the same derivation as the first
 * load, rather than a second opinion about the same data.
 */
const buildSteps = (data, myUserId, reward, hasVisitedDashboard, canSeeDashboard) => {
    const positions = data?.positions.filter((p) => p.archived !== true) ?? [];
    // the manager who created the workspace is a member of it from the first second, so
    // „first employees added" can only mean someone other than the person looking at this
    const employees = data?.members.filter((m) => m.userId !== myUserId) ?? [];
    const withContract = employees.filter((m) => (m.roles ?? []).some((r) => (r.contracts ?? []).length > 0));
    const allSteps = [
        {
            key: 'POSITIONS',
            id: STEP_KEYS.POSITIONS,
            titleKey: 'ONB_STEP_POSITIONS_TITLE',
            descKey: 'ONB_STEP_POSITIONS_DESC',
            link: '/workspace/job-positions',
            ico: _ui_1.Icon.ICONS.positions,
            isDone: positions.length > 0,
            autoTagKey: 'ONB_AUTO_POSITIONS_X',
            autoCount: positions.length,
        },
        {
            key: 'EMPLOYEES',
            id: STEP_KEYS.EMPLOYEES,
            titleKey: 'ONB_STEP_EMPLOYEES_TITLE',
            descKey: 'ONB_STEP_EMPLOYEES_DESC',
            link: '/workspace/employees',
            ico: _ui_1.Icon.ICONS.personAdd,
            isDone: employees.length > 0,
            autoTagKey: 'ONB_AUTO_EMPLOYEES_X',
            autoCount: employees.length,
        },
        {
            key: 'CONTRACTS',
            id: STEP_KEYS.CONTRACTS,
            titleKey: 'ONB_STEP_CONTRACTS_TITLE',
            descKey: 'ONB_STEP_CONTRACTS_DESC',
            link: '/workspace/employees',
            ico: _ui_1.Icon.ICONS.shift,
            isDone: withContract.length > 0,
            autoTagKey: 'ONB_AUTO_CONTRACTS_X',
            autoCount: withContract.length,
        },
        {
            key: 'SCHEDULE',
            id: STEP_KEYS.SCHEDULE,
            titleKey: 'ONB_STEP_SCHEDULE_TITLE',
            descKey: 'ONB_STEP_SCHEDULE_DESC',
            link: '/schedule/',
            ico: _ui_1.Icon.ICONS.calendar,
            // the uncommitted (working) view, so an unpublished draft already counts
            isDone: (data?.draftShifts.length ?? 0) > 0,
        },
        {
            key: 'PUBLISH',
            id: STEP_KEYS.PUBLISH,
            titleKey: 'ONB_STEP_PUBLISH_TITLE',
            descKey: 'ONB_STEP_PUBLISH_DESC',
            link: '/schedule/',
            ico: _ui_1.Icon.ICONS.publish,
            // the default (published) view — what employees can actually see
            isDone: (data?.publishedShifts.length ?? 0) > 0,
        },
        {
            key: 'TERMINAL',
            id: STEP_KEYS.TERMINAL,
            titleKey: 'ONB_STEP_TERMINAL_TITLE',
            descKey: 'ONB_STEP_TERMINAL_DESC',
            link: '/backoffice/terminals',
            ico: _ui_1.Icon.ICONS.terminal,
            isDone: (data?.terminals.length ?? 0) > 0,
        },
        {
            key: 'DASHBOARD',
            id: STEP_KEYS.DASHBOARD,
            titleKey: 'ONB_STEP_DASHBOARD_TITLE',
            descKey: 'ONB_STEP_DASHBOARD_DESC',
            link: '/dashboard',
            ico: _ui_1.Icon.ICONS.dashboard,
            // the one step that is not derived from workspace data: reading the dashboard leaves
            // nothing behind on the server, so the visit is recorded in the onboarding slice
            // instead (see `visitedOnboardingDashboard`)
            isDone: hasVisitedDashboard,
        },
        {
            ...exports.REWARD_STEP,
            isDone: reward?.state === onboarding_reward_1.ONBOARDING_REWARD_STATES.CLAIMED,
        },
    ];
    // No reward means the reward step is not part of this checklist at all — no coupon for the
    // organization's currency, one already expired, or the manager is not an organization
    // owner. `useOnboardingReward` has already collapsed every one of those to `undefined`.
    // A manager whose role lacks the dashboard permission would get a step leading to NotFound.
    return allSteps.filter((step) => !HIDDEN_STEPS.includes(step.id) &&
        (step.isReward !== true || (0, util_1.isValue)(reward)) &&
        (step.id !== STEP_KEYS.DASHBOARD || canSeeDashboard));
};
/**
 * What the hook answers while it is switched off. `isComplete` is hard-coded rather than
 * derived: `completedCount === steps.length` is `0 === 0` on an empty list, and every caller
 * reads that as „the whole checklist is finished".
 */
const IDLE = {
    isLoading: true,
    steps: [],
    completedCount: 0,
    totalCount: 0,
    activeIndex: -1,
    isComplete: false,
};
/**
 * `enabled` is what keeps this off the wire. The hook has a single owner mounted above the
 * router (`OnboardingProvider`), which is what lets the checklist be watched from any page —
 * and the flag is what stops that from meaning „one query per app load for everybody".
 * Disabled it makes no request and subscribes to nothing.
 */
const useOnboardingSteps = (enabled, reward) => {
    const me = (0, useMe_1.useMe)();
    const { hasReadPermission: canSeeDashboard } = (0, util_1.useAccessRole)(permissions_pool_1.PERMISSIONS.MNG_DASHBOARD);
    const workspaceId = (0, store_1.useRootSelector)((state) => state.workspaceId);
    const hasVisitedDashboard = (0, store_1.useRootSelector)((state) => (0, util_1.isValue)(workspaceId)
        ? state.onboarding[workspaceId]?.visitedDashboard === true
        : false);
    const [state, dispatch] = react_1.default.useReducer(reducer, initialState);
    react_1.default.useEffect(() => {
        if (!enabled || !(0, util_1.isValue)(workspaceId))
            return;
        const action = async () => {
            dispatch({ type: 'LOADING_START' });
            const data = await (0, onboarding_state_1.default)({ id: workspaceId });
            // a failed query answers `workspace: null`, and `isDefined(null)` is true
            dispatch({ type: 'LOADING_END', payload: (0, util_1.isValue)(data) ? data : undefined });
        };
        action();
    }, [enabled, workspaceId]);
    // Push, not polling: every change a step is derived from already comes down the websocket
    // (see `app/util/onboarding-signal.ts`), so the checklist re-reads itself when one lands
    // and stays completely idle otherwise. `enabled` is what keeps it off the wire entirely
    // for a manager who has neither opened the checklist nor started a step.
    react_1.default.useEffect(() => {
        if (!enabled || !(0, util_1.isValue)(workspaceId))
            return;
        let isCancelled = false;
        let settle;
        const refresh = async () => {
            const data = await (0, onboarding_state_1.default)({ id: workspaceId });
            // a failed query answers `workspace: null`, and `isDefined(null)` is true — keep the
            // checklist that is already on screen rather than blanking it
            if (isCancelled || !(0, util_1.isValue)(data))
                return;
            dispatch({ type: 'REFRESHED', payload: data });
        };
        const onChange = () => {
            window.clearTimeout(settle);
            settle = window.setTimeout(() => {
                void refresh();
            }, SETTLE_MS);
        };
        const unsubscribe = (0, onboarding_signal_1.onOnboardingRelevantChange)(onChange);
        return () => {
            isCancelled = true;
            unsubscribe();
            window.clearTimeout(settle);
        };
    }, [enabled, workspaceId]);
    const steps = buildSteps(state.data, me?.id, reward, hasVisitedDashboard, canSeeDashboard);
    const completedCount = steps.filter((s) => s.isDone).length;
    const activeIndex = steps.findIndex((s) => !s.isDone);
    if (!enabled)
        return IDLE;
    return {
        isLoading: state.isLoading,
        steps,
        completedCount,
        totalCount: steps.length,
        activeIndex,
        isComplete: completedCount === steps.length,
    };
};
exports.useOnboardingSteps = useOnboardingSteps;
